import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Award,
  BookOpen,
  CheckCircle,
  Clock,
  HelpCircle,
  Lock,
  LucideIcon,
  MapPin,
  MessageCircle,
  Plus,
  Search,
  Ticket,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNER_TICKETS_PERMISSION } from '@/constants/constant-permissions';
import { LEARNER_TICKET_CREATE, LEARNER_TICKET_CHAT } from '@/constants/constant-routes';

import Modal from '@/components/ui/Modal';
import Pagination from '@/components/ui/Pagination';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import { SkeletonLoader, TableSkeleton } from '@/components/ui/SkeletonLoader';
import { businessService } from '@/services/business.service';
import { ticketService } from '@/services/ticket.service';
import {
  SelfTicketItemType,
  SelfTicketRequestResponseDto,
  SelfTicketRequestStatus,
} from '@/types/ticket.types';

// Type config interface
interface TypeConfig {
  icon: LucideIcon;
  label: string;
  gradient: string;
  iconBg: string;
  iconColor: string;
  itemBg: string;
  itemBorder: string;
}

// Status config interface
interface StatusConfig {
  bg: string;
  text: string;
  icon: LucideIcon;
  label: string;
}

// Type configurations map
const typeConfigMap: Record<SelfTicketItemType, TypeConfig> = {
  [SelfTicketItemType.COURSE]: {
    icon: BookOpen,
    label: 'Course',
    gradient: 'from-brand-indigo-500 to-brand-purple-600',
    iconBg: 'bg-brand-indigo-100',
    iconColor: 'text-brand-indigo-600',
    itemBg: 'bg-brand-indigo-50',
    itemBorder: 'border-brand-indigo-100',
  },
  [SelfTicketItemType.CERTIFICATION]: {
    icon: Award,
    label: 'Certification',
    gradient: 'from-brand-amber-500 to-brand-orange-600',
    iconBg: 'bg-brand-amber-100',
    iconColor: 'text-brand-amber-600',
    itemBg: 'bg-brand-amber-50',
    itemBorder: 'border-brand-amber-100',
  },
  [SelfTicketItemType.LEARNING_PATH]: {
    icon: MapPin,
    label: 'Learning Path',
    gradient: 'from-brand-green-500 to-brand-emerald-600',
    iconBg: 'bg-brand-green-100',
    iconColor: 'text-brand-green-600',
    itemBg: 'bg-brand-green-50',
    itemBorder: 'border-brand-green-100',
  },
  [SelfTicketItemType.GENERAL]: {
    icon: HelpCircle,
    label: 'General',
    gradient: 'from-brand-purple-500 to-brand-violet-600',
    iconBg: 'bg-brand-purple-100',
    iconColor: 'text-brand-purple-600',
    itemBg: 'bg-brand-purple-50',
    itemBorder: 'border-brand-purple-100',
  },
};

// Status configurations map
const statusConfigMap: Record<SelfTicketRequestStatus, StatusConfig> = {
  [SelfTicketRequestStatus.PENDING]: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    icon: Clock,
    label: 'Pending',
  },
  [SelfTicketRequestStatus.APPROVED]: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    icon: CheckCircle,
    label: 'Approved',
  },
  [SelfTicketRequestStatus.DENIED]: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    icon: XCircle,
    label: 'Denied',
  },
  [SelfTicketRequestStatus.CLOSED]: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    icon: Lock,
    label: 'Closed',
  },
};

// Status filter options
const statusFilterOptions: SelectOption[] = [
  { label: 'All Status', value: '' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Denied', value: 'DENIED' },
  { label: 'Closed', value: 'CLOSED' },
];

// Item type filter options
const itemTypeFilterOptions: SelectOption[] = [
  { label: 'All Types', value: '' },
  { label: 'Course', value: SelfTicketItemType.COURSE },
  { label: 'Certification', value: SelfTicketItemType.CERTIFICATION },
  { label: 'Learning Path', value: SelfTicketItemType.LEARNING_PATH },
  { label: 'General', value: SelfTicketItemType.GENERAL },
];

// Format date to dd/mm/yyyy
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Status badge component
const StatusBadge = ({ status }: { status: SelfTicketRequestStatus }): JSX.Element => {
  const config = statusConfigMap[status];
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Item type badge component
const ItemTypeBadge = ({ type }: { type: SelfTicketItemType }): JSX.Element => {
  const config = typeConfigMap[type];
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.iconBg} ${config.iconColor}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Page skeleton for initial loading
const PageSkeleton = (): JSX.Element => (
  <div className="space-y-6">
    {/* Header skeleton */}
    <div className="bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 rounded-xl p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <SkeletonLoader type="custom" className="w-8 h-8 rounded bg-white/20" />
            <SkeletonLoader type="custom" className="h-8 w-48 rounded bg-white/20" />
          </div>
          <SkeletonLoader type="custom" className="h-4 w-56 sm:w-96 rounded bg-white/20" />
        </div>
        <SkeletonLoader type="custom" className="h-10 w-32 rounded-lg bg-white/20" />
      </div>
    </div>

    {/* Table card skeleton */}
    <div className="bg-white rounded-xl shadow-sm border">
      {/* Filters skeleton */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex flex-col sm:flex-row gap-4">
          <SkeletonLoader type="custom" className="h-10 flex-1 rounded-lg" />
          <SkeletonLoader type="custom" className="h-10 w-full sm:w-[180px] rounded-lg" />
        </div>
      </div>
      {/* Table skeleton */}
      <TableSkeleton rows={5} columns={6} showHeader={true} />
    </div>
  </div>
);

// Feature Disabled Component
const FeatureDisabled = (): JSX.Element => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <AlertCircle className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">Feature Not Available</h3>
    <p className="text-gray-500 text-center max-w-md">
      Self-ticket assignment has not been enabled by your organization administrator.
      Please contact your administrator to enable this feature.
    </p>
  </div>
);

// Items Detail Modal Props
interface ItemsDetailModalProps {
  ticket: SelfTicketRequestResponseDto | null;
  isOpen: boolean;
  onClose: () => void;
}

// Items Detail Modal Content
const ItemsDetailModal = ({ ticket, isOpen, onClose }: ItemsDetailModalProps): JSX.Element | null => {
  if (!ticket) return null;

  const typeConfig = typeConfigMap[ticket.item_type];
  const statusConfig = statusConfigMap[ticket.status];
  const TypeIcon = typeConfig.icon;
  const StatusIcon = statusConfig.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Ticket Details"
      size="md"
    >
      <div className="space-y-5">
        {/* Status & Type Row */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${typeConfig.iconBg}`}>
              <TypeIcon className={`w-5 h-5 ${typeConfig.iconColor}`} />
            </div>
            <span className="text-sm font-medium text-gray-700">{typeConfig.label} Request</span>
          </div>
          <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${statusConfig.bg} ${statusConfig.text}`}>
            <StatusIcon className="w-3.5 h-3.5" />
            {statusConfig.label}
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Requested By</p>
            <p className="font-medium text-gray-900">{ticket.requester_name}</p>
            <p className="text-sm text-gray-500 truncate">{ticket.requester_email}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Date</p>
            <p className="font-medium text-gray-900">{formatDate(ticket.createdAt)}</p>
            <p className="text-sm text-gray-500">{ticket.items_count} item{ticket.items_count > 1 ? 's' : ''}</p>
          </div>
        </div>

        {/* For GENERAL tickets - show description, for others - show items */}
        {ticket.item_type === SelfTicketItemType.GENERAL ? (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Request Description</p>
            <div className="p-3 bg-brand-purple-50 rounded-lg border border-brand-purple-200">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {ticket.general_description || 'No description provided'}
              </p>
            </div>
            {/* Show Suggested Items if ticket has been actioned */}
            {ticket.actioned_by?.action === 'SUGGESTED' && ticket.items && ticket.items.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Suggested Items</p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {ticket.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-brand-purple-50 rounded-lg border border-brand-purple-200"
                    >
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-purple-100 flex items-center justify-center">
                        <span className="text-xs font-semibold text-brand-purple-600">{index + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.item_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Requested Items</p>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {ticket.items.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-xs font-semibold text-gray-600">{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{item.item_name}</p>
                    {item.item_id && !item.item_id.startsWith('ITEM_') && (
                      <p className="text-xs text-gray-500">ID: {item.item_id}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Request Message - only for non-GENERAL tickets */}
        {ticket.item_type !== SelfTicketItemType.GENERAL && ticket.request_message && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Message</p>
            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-200">
              {ticket.request_message}
            </p>
          </div>
        )}

        {/* Resolution */}
        {(ticket.approved_by || ticket.denied_by) && (
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Resolution</p>
            {ticket.approved_by && (
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Approved by {ticket.approved_by.user_name}</p>
                  <p className="text-xs text-gray-500">{ticket.approved_by.role}</p>
                </div>
              </div>
            )}
            {ticket.denied_by && (
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Denied by {ticket.denied_by.user_name}</p>
                  <p className="text-xs text-gray-500">{ticket.denied_by.role}</p>
                  {ticket.denied_by.reason && (
                    <p className="text-sm text-red-700 mt-2">Reason: {ticket.denied_by.reason}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

// Main Component
export default function LearnerTickets(): JSX.Element {
  const { LEARNER_TICKETS_READ, LEARNER_TICKETS_WRITE } = LEARNER_TICKETS_PERMISSION;
  usePermissionRouteAccess([LEARNER_TICKETS_READ, LEARNER_TICKETS_WRITE]);
  const canWrite = useHasPermission([LEARNER_TICKETS_WRITE]);

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, DEFAULT_DEBOUNCE_DELAY);
  const [statusFilter, setStatusFilter] = useState<SelfTicketRequestStatus | ''>('');
  const [itemTypeFilter, setItemTypeFilter] = useState<SelfTicketItemType | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedTicket, setSelectedTicket] = useState<SelfTicketRequestResponseDto | null>(null);

  // Check if feature is enabled via business settings
  const { data: businessSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['businessSettings'],
    queryFn: () => businessService.getBusinessSettings(),
  });

  const isFeatureEnabled = businessSettings?.data?.enable_self_ticket_assignment;

  // Fetch user's ticket requests
  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: ['my-ticket-requests', currentPage, itemsPerPage, statusFilter, itemTypeFilter, debouncedSearch],
    queryFn: () =>
      ticketService.getMyRequests({
        page: currentPage,
        limit: itemsPerPage,
        status: statusFilter || undefined,
        item_type: itemTypeFilter || undefined,
        search: debouncedSearch || undefined,
      }),
    enabled: isFeatureEnabled,
  });

  // Handle page change
  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (perPage: number): void => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, itemTypeFilter, debouncedSearch]);

  // Loading state
  if (settingsLoading) {
    return <PageSkeleton />;
  }

  // Feature disabled
  if (!isFeatureEnabled) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 rounded-xl p-4 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Ticket className="w-8 h-8" />
                <h1 className="text-2xl font-bold">List of Tickets</h1>
              </div>
              <p className="text-brand-purple-100">
                Raise a ticket to request your manager or admin to assign you a course, certification, or learning path
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border">
          <FeatureDisabled />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header — stacks on mobile so the description gets the full width and
          "Create Ticket" becomes a full-width button below it. */}
      <div className="bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 rounded-xl p-4 sm:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
              <Ticket className="w-6 h-6 sm:w-8 sm:h-8" />
              <h1 className="text-xl sm:text-2xl font-bold">List of Tickets</h1>
            </div>
            <p className="text-brand-purple-100 text-sm sm:text-base">
              Raise a ticket to request your manager or admin to assign you a course, certification, or learning path
            </p>
          </div>
          <button
            onClick={() => canWrite && navigate(LEARNER_TICKET_CREATE)}
            disabled={!canWrite}
            title={!canWrite ? 'You do not have permission to create tickets' : undefined}
            className={`w-full sm:w-auto flex-shrink-0 whitespace-nowrap flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-white text-brand-purple-600 rounded-lg font-medium transition-colors ${
              !canWrite ? 'opacity-50 cursor-not-allowed' : 'hover:bg-brand-purple-50'
            }`}
          >
            <Plus className="w-5 h-5" />
            Create Ticket
          </button>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-xl shadow-sm border">
        {/* Filters */}
        <div className="p-3 sm:p-4 border-b bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by ticket no or request..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Filters: side by side, half width each on mobile; fixed 180px from sm */}
            <div className="grid grid-cols-2 gap-3 sm:flex sm:gap-4">
            {/* Item Type Filter */}
            <div className="min-w-0 sm:w-[180px]">
              <SimpleSelect
                value={itemTypeFilter}
                onChange={(value) => setItemTypeFilter((value as SelfTicketItemType | '') || '')}
                options={itemTypeFilterOptions}
                placeholder="All Types"
                isSearchable={false}
                isClearable={false}
              />
            </div>

            {/* Status Filter */}
            <div className="min-w-0 sm:w-[180px]">
              <SimpleSelect
                value={statusFilter}
                onChange={(value) => setStatusFilter((value as SelfTicketRequestStatus | '') || '')}
                options={statusFilterOptions}
                placeholder="All Status"
                isSearchable={false}
                isClearable={false}
              />
            </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {ticketsLoading ? (
            <TableSkeleton rows={5} columns={6} showHeader={true} />
          ) : ticketsData?.data && ticketsData.data.length > 0 ? (
            <>
            {/* Mobile: one card per ticket. A 6-column table only fits a phone
                by scrolling sideways, which hides the status and Chat action. */}
            <ul className="md:hidden divide-y divide-gray-200">
              {ticketsData.data.map((ticket: SelfTicketRequestResponseDto) => (
                <li key={ticket._id} className="p-4 space-y-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-mono text-sm font-semibold text-primary-600 break-all">
                      {ticket.ticket_number}
                    </span>
                    <StatusBadge status={ticket.status} />
                  </div>

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <ItemTypeBadge type={ticket.item_type} />
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatDate(ticket.createdAt)}
                    </span>
                  </div>

                  {ticket.item_type === SelfTicketItemType.GENERAL ? (
                    <div>
                      <p className="text-sm text-gray-900 line-clamp-2">
                        {ticket.general_description || 'No description'}
                      </p>
                      {ticket.general_description && ticket.general_description.length > 50 && (
                        <button
                          onClick={() => setSelectedTicket(ticket)}
                          className="text-xs text-brand-purple-600 font-medium hover:text-brand-purple-800 hover:underline mt-1"
                        >
                          Show more
                        </button>
                      )}
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-900 line-clamp-2">{ticket.items[0]?.item_name}</p>
                      {ticket.items_count > 1 && (
                        <button
                          onClick={() => setSelectedTicket(ticket)}
                          className="text-xs text-primary-600 font-medium hover:text-primary-800 hover:underline mt-1"
                        >
                          +{ticket.items_count - 1} more
                        </button>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-3 pt-1">
                    <button
                      onClick={() => canWrite && navigate(`${LEARNER_TICKET_CHAT}/${ticket._id}/chat`)}
                      disabled={!canWrite}
                      title={!canWrite ? 'You do not have permission to chat' : undefined}
                      className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg transition-colors ${
                        !canWrite ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-100'
                      }`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      Chat
                    </button>
                    {ticket.approved_by && (
                      <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
                        <CheckCircle className="w-4 h-4" />
                        Approved
                      </span>
                    )}
                    {ticket.denied_by && (
                      <span className="flex items-center gap-1 text-red-600 text-xs font-medium">
                        <XCircle className="w-4 h-4" />
                        Denied
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* md and up: the original table */}
            <table className="hidden md:table w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Ticket No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Requested For
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Requested Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Ticket Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {ticketsData.data.map((ticket: SelfTicketRequestResponseDto) => (
                  <tr key={ticket._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-semibold text-primary-600">
                        {ticket.ticket_number}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <ItemTypeBadge type={ticket.item_type} />
                    </td>
                    <td className="px-4 py-3">
                      {ticket.item_type === SelfTicketItemType.GENERAL ? (
                        <div>
                          <p className="text-sm text-gray-900 line-clamp-1">
                            {ticket.general_description || 'No description'}
                          </p>
                          {ticket.general_description && ticket.general_description.length > 50 && (
                            <button
                              onClick={() => setSelectedTicket(ticket)}
                              className="text-xs text-brand-purple-600 font-medium hover:text-brand-purple-800 hover:underline mt-1"
                            >
                              Show more
                            </button>
                          )}
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm text-gray-900">{ticket.items[0]?.item_name}</p>
                          {ticket.items_count > 1 && (
                            <button
                              onClick={() => setSelectedTicket(ticket)}
                              className="text-xs text-primary-600 font-medium hover:text-primary-800 hover:underline mt-1"
                            >
                              +{ticket.items_count - 1} more
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Clock className="w-3 h-3" />
                        {formatDate(ticket.createdAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={ticket.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => canWrite && navigate(`${LEARNER_TICKET_CHAT}/${ticket._id}/chat`)}
                          disabled={!canWrite}
                          title={!canWrite ? 'You do not have permission to chat' : undefined}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg transition-colors ${
                            !canWrite ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-100'
                          }`}
                        >
                          <MessageCircle className="w-4 h-4" />
                          Chat
                        </button>
                        {ticket.approved_by && (
                          <span className="flex items-center gap-1 text-green-600 text-xs">
                            <CheckCircle className="w-3.5 h-3.5" />
                          </span>
                        )}
                        {ticket.denied_by && (
                          <span className="flex items-center gap-1 text-red-600 text-xs">
                            <XCircle className="w-3.5 h-3.5" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center text-gray-500">
              <Ticket className="w-12 h-12 mb-3 opacity-50" />
              <p className="font-medium">No tickets found</p>
              <p className="text-sm">
                {statusFilter ? 'Try adjusting your filters' : 'Click "Create Ticket" to raise a new request'}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {ticketsData?.pagination && ticketsData.pagination.total_pages > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={ticketsData.pagination.total_pages}
            totalItems={ticketsData.pagination.total}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            showItemsPerPage={true}
            itemsPerPageOptions={[10, 20, 50]}
          />
        )}
      </div>

      {/* Items Detail Modal */}
      <ItemsDetailModal
        ticket={selectedTicket}
        isOpen={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
      />
    </div>
  );
}
